/**
 * SMS central socket message router.
 *
 * Mounts below SocketProvider + the React Query client provider and routes
 * every inbound message to the matching handler in `socket-handlers/`.
 * Adding a new reunification or alert event type means adding it to the
 * registry — not patching this file or any hook.
 *
 * On every successful WebSocket (re)connect this router also fires a
 * single `GET /api/v2/alerts/snapshot/` and feeds the response
 * back through the same registry. This lets a client that was offline
 * during an alert lifecycle or a reunification transition recover the
 * coherent current state in one HTTP round-trip — reliable-broadcast replay
 * covers in-flight delivery only within its Redis TTL and only for clients that
 * declared `supports_ack: true`, so the snapshot is the canonical
 * recovery mechanism (ADR-100 §4, ADR-106 §4, ADR-078 no-poll rule).
 *
 * Dedupe: we track `requires_ack` message ids seen during the current
 * connection. The server may replay a message on reconnect that the
 * snapshot has already reconciled; skipping by id avoids redundant
 * React Query invalidations. The set resets each time a new WS
 * connection comes up (server-side replay buffer is per-connection).
 *
 * i18n (ADR-129 §6): localized per-language re-broadcast frames
 * are intercepted ahead of the registry and swapped into the React Query
 * cache — see `socket-handlers/alertLocalization.ts` for the contract.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@aegix/auth-identity';
import { useActiveOrganization } from '@aegix/auth-organization';
import type { ActiveOrganization, SMSUser } from '../lib/sms-auth';
import { useSocket, useSocketMessage } from './SocketContext';
import { useOptionalReunificationJoinPrompt } from './ReunificationJoinPromptContext';
import {
  createSocketHandlers,
  dispatchSnapshot,
  parseSocketMessage,
  type HandlerDeps,
} from './socket-handlers';
import { AlertLocalizationTracker } from '@tiaki/alert-localization';
import { processAlertLocalizationFrame } from './socket-handlers/alertLocalization';
import { fetchAlertSnapshot } from '../services/snapshot.service';

export function SocketMessageRouter({ children }: PropsWithChildren) {
  const user = useUser<SMSUser>();
  // The SMS school-id is always a numeric Postgres FK; the lib widens
  // `Organization.id` to `string | number` for future GUID support.
  // Narrow back so the handler deps + snapshot fetch keep their
  // existing `number | null` shape.
  const activeOrganization =
    useActiveOrganization() as ActiveOrganization | null;
  const queryClient = useQueryClient();
  const { connectionState, isConnected } = useSocket();
  // Optional: degrades gracefully for test harnesses that mount the router
  // without the prompt provider. In the real app the provider is always
  // present (see App.tsx), so enqueue is wired end-to-end.
  const promptCtx = useOptionalReunificationJoinPrompt();
  const enqueueJoinPrompt = promptCtx?.enqueuePrompt;

  const handlerDeps = useMemo<HandlerDeps>(
    () => ({
      queryClient,
      currentUserId: user?.id ?? null,
      schoolId: activeOrganization?.id ?? null,
      enqueueJoinPrompt,
    }),
    [queryClient, user?.id, activeOrganization?.id, enqueueJoinPrompt],
  );

  const handlers = useMemo(() => createSocketHandlers(handlerDeps), [handlerDeps]);

  // Per-connection dedupe: resets whenever a new WebSocket comes up so the
  // server's reliable-broadcast replay IDs from the previous
  // connection do not mask legitimate new frames on the current one.
  const seenMessageIdsRef = useRef<Set<string>>(new Set());

  // ADR-129 §6: tracks the operational-frame accept window per
  // alert so a following localized re-broadcast can be matched and
  // stale-dropped. Created once (stable across renders); timers are
  // cancelled on unmount. Mirrors AIM's SocketMessageRouter wiring.
  const [alertLocalizationTracker] = useState(
    () => new AlertLocalizationTracker(),
  );
  useEffect(
    () => () => alertLocalizationTracker.dispose(),
    [alertLocalizationTracker],
  );

  const handleSocketMessage = useCallback(
    (raw: string) => {
      const parsed = parseSocketMessage(raw);
      if (!parsed) return;

      // ADR-129 §6: localized re-broadcast frames carry `language`
      // + `translation_version`. They are a strictly-additive enhancement and
      // must NEVER flow through the handler registry — replaying an alert
      // frame's side effects for a translation is wrong by contract. Checked
      // BEFORE the id-dedupe: localized frames carry no ack id, and a replayed
      // operational Alert frame must still arm the accept window even when
      // the dedupe below skips its handler dispatch.
      if (
        processAlertLocalizationFrame(parsed, alertLocalizationTracker, queryClient)
      ) {
        return;
      }

      // Dedupe by reliable-broadcast message id — `requires_ack` frames carry an `id`
      // that the server replays as-is on reconnect. If we've already
      // processed that id on this connection, skip. Non-critical frames
      // have no `id` and flow through unchanged.
      const msgId = parsed.raw['id'];
      if (typeof msgId === 'string' && msgId.length > 0) {
        if (seenMessageIdsRef.current.has(msgId)) return;
        seenMessageIdsRef.current.add(msgId);
      } else if (typeof msgId === 'number') {
        const key = `n:${msgId}`;
        if (seenMessageIdsRef.current.has(key)) return;
        seenMessageIdsRef.current.add(key);
      }

      const handler = handlers[parsed.type];
      if (!handler) return; // unhandled types ignored — matches AIM
      try {
        void handler(parsed.data, parsed.raw);
      } catch (err) {
        console.error(`[SocketRouter] error in ${parsed.type} handler:`, err);
      }
    },
    [handlers, alertLocalizationTracker, queryClient],
  );

  useSocketMessage(handleSocketMessage);

  // Snapshot-on-(re)connect. The dep is `lastConnected` (a fresh Date on
  // every open) so the effect fires on the initial connect AND every
  // subsequent reconnect — but exactly once per connect event. A token
  // guards against stale responses landing after a second reconnect
  // superseded them.
  const lastConnected = connectionState.lastConnected;
  const connectTokenRef = useRef(0);
  const schoolIdForFetch = activeOrganization?.id ?? null;

  useEffect(() => {
    if (!isConnected || !lastConnected) return;

    // New connection → clear per-connection state before we fetch the
    // snapshot. Done here rather than in `onopen` because the router
    // owns the dedupe set, not the provider.
    seenMessageIdsRef.current = new Set();

    const myToken = ++connectTokenRef.current;

    void (async () => {
      try {
        const snapshot = await fetchAlertSnapshot(schoolIdForFetch);
        if (connectTokenRef.current !== myToken) return; // superseded
        dispatchSnapshot(snapshot, handlers, handlerDeps);
      } catch (err) {
        // Non-fatal: next reconnect will retry. Log without surfacing —
        // an offline snapshot failure must not crash the WS lifecycle.
        console.warn('[SocketRouter] snapshot fetch failed:', err);
      }
    })();
  }, [isConnected, lastConnected, schoolIdForFetch, handlers, handlerDeps]);

  return <>{children}</>;
}

export default SocketMessageRouter;
